import React, { useState, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

/* ═══════════════════════════════════════
   BoardDetail — 글 상세 + 댓글 (v2)
   ═══════════════════════════════════════ */

const slideDown = keyframes`
  from { opacity: 0; max-height: 0; transform: translateY(-12px); }
  to   { opacity: 1; max-height: 5000px; transform: translateY(0); }
`;

// ── Wrapper ──
const DetailWrapper = styled.div`
  background: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  box-shadow: ${({ theme }) => theme.elevation.md};
  overflow: hidden;
  margin-bottom: 24px;
  animation: ${slideDown} 0.4s ease both;
`;

// ── Header ──
const DetailHeader = styled.div`
  padding: 24px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  background: ${({ theme }) => theme.colors.neutral[50]};

  @media (max-width: 640px) {
    padding: 18px 16px;
  }
`;

const TitleRow = styled.h2`
  margin: 0 0 14px;
  font-family: ${({ theme }) => theme.typography.fontFamily.semibold};
  font-size: 1.35rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[900]};
  line-height: 1.4;
  word-break: keep-all;

  @media (max-width: 640px) {
    font-size: 1.15rem;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const MetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.neutral[500]};

  @media (max-width: 640px) {
    gap: 10px;
    font-size: 0.8rem;
  }
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const AuthorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.primary[50]};
  color: ${({ theme }) => theme.colors.primary[700]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.82rem;
  font-weight: 600;
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const ActionBtn = styled.button`
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transition.fast};
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  background: ${({ theme }) => theme.colors.neutral[0]};
  color: ${({ theme }) => theme.colors.neutral[600]};

  &:hover {
    border-color: ${({ theme }) => theme.colors.neutral[300]};
    background: ${({ theme }) => theme.colors.neutral[50]};
  }

  ${({ $variant, theme }) =>
    $variant === 'danger' &&
    `
    color: ${theme.colors.semantic.error};
    &:hover {
      background: ${theme.colors.semantic.error};
      color: #fff;
      border-color: ${theme.colors.semantic.error};
    }
  `}

  ${({ $variant, theme }) =>
    $variant === 'primary' &&
    `
    background: ${theme.colors.primary[500]};
    color: #fff;
    border-color: ${theme.colors.primary[500]};
    &:hover {
      background: ${theme.colors.primary[700]};
      border-color: ${theme.colors.primary[700]};
    }
  `}
`;

const CloseBtn = styled(ActionBtn)`
  @media (max-width: 640px) {
    padding: 6px 10px;
  }
`;

// ── Body ──
const DetailBody = styled.div`
  padding: 28px;
  min-height: 120px;
  font-size: 0.95rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.neutral[800]};
  word-break: break-word;

  /* 교육용 콘텐츠 스타일 */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 24px;
    margin-bottom: 12px;
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-family: ${({ theme }) => theme.typography.fontFamily.semibold};
  }

  p {
    margin: 0 0 12px;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${({ theme }) => theme.borderRadius.base};
    margin: 12px 0;
  }

  a {
    color: ${({ theme }) => theme.colors.primary[600]};
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  pre, code {
    font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  }

  code {
    padding: 2px 6px;
    background: ${({ theme }) => theme.colors.neutral[100]};
    border-radius: 4px;
    font-size: 0.88em;
    color: ${({ theme }) => theme.colors.semantic.error};
  }

  pre {
    background: ${({ theme }) => theme.colors.neutral[900]};
    color: #e5e7eb;
    padding: 20px;
    border-radius: ${({ theme }) => theme.borderRadius.base};
    overflow-x: auto;
    margin: 16px 0;
    font-size: 0.88rem;
    line-height: 1.6;

    code {
      background: none;
      color: inherit;
      padding: 0;
      border-radius: 0;
    }
  }

  blockquote {
    border-left: 4px solid ${({ theme }) => theme.colors.primary[300]};
    margin: 16px 0;
    padding: 12px 20px;
    background: ${({ theme }) => theme.colors.primary[50]};
    border-radius: 0 ${({ theme }) => theme.borderRadius.base} ${({ theme }) => theme.borderRadius.base} 0;
    color: ${({ theme }) => theme.colors.neutral[700]};
  }

  ul, ol {
    padding-left: 24px;
    margin: 12px 0;
  }

  li {
    margin-bottom: 4px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 0.9rem;

    th, td {
      padding: 10px 14px;
      border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
      text-align: left;
    }

    th {
      background: ${({ theme }) => theme.colors.neutral[50]};
      font-weight: 600;
    }
  }

  @media (max-width: 640px) {
    padding: 18px 16px;
    font-size: 0.9rem;

    pre {
      padding: 14px;
      font-size: 0.82rem;
    }

    table {
      display: block;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
`;

// ── Comments Section ──
const CommentsSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  background: ${({ theme }) => theme.colors.neutral[50]};
`;

const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 18px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[800]};

  @media (max-width: 640px) {
    padding: 14px 16px;
    font-size: 0.9rem;
  }
`;

const CommentCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 6px;
  background: ${({ theme }) => theme.colors.primary[500]};
  color: #fff;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 700;
`;

const CommentList = styled.div`
  padding: 0 28px;

  @media (max-width: 640px) {
    padding: 0 16px;
  }
`;

const CommentItem = styled.div`
  padding: 16px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  }
`;

const CommentTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.neutral[700]};
`;

const CommentTime = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.neutral[400]};
`;

const CommentBody = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.neutral[700]};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const CommentDeleteBtn = styled.button`
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 500;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.neutral[400]};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transition.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.error};
    border-color: ${({ theme }) => theme.colors.semantic.error};
  }
`;

// ── Comment Input ──
const CommentInputWrapper = styled.div`
  padding: 18px 28px;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral[100]};

  @media (max-width: 640px) {
    padding: 14px 16px;
  }
`;

const CommentInputArea = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const CommentTextarea = styled.textarea`
  flex: 1;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.neutral[800]};
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  transition: border-color ${({ theme }) => theme.transition.fast},
              box-shadow ${({ theme }) => theme.transition.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary[50]};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral[400]};
  }
`;

const CommentSubmitBtn = styled.button`
  align-self: flex-end;
  padding: 10px 20px;
  font-size: 0.88rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.primary[500]};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary[700]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const LoginNotice = styled.div`
  padding: 16px 28px;
  text-align: center;
  color: ${({ theme }) => theme.colors.neutral[400]};
  font-size: 0.88rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral[100]};

  @media (max-width: 640px) {
    padding: 14px 16px;
  }
`;

// ── Component ──
function BoardDetail({
  post,
  comments = [],
  session,
  onClose,
  onEdit,
  onDelete,
  onInsertComment,
  onDeleteComment,
}) {
  const [commentText, setCommentText] = useState('');
  const commentRef = useRef(null);

  const isOwner = session?.Name === post?.notice_name;
  const isLoggedIn = !!session?.UserId;

  const handleSubmitComment = useCallback(async () => {
    if (!commentText.trim()) return;
    await onInsertComment?.(commentText);
    setCommentText('');
    if (commentRef.current) commentRef.current.value = '';
  }, [commentText, onInsertComment]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        handleSubmitComment();
      }
    },
    [handleSubmitComment]
  );

  if (!post) return null;

  return (
    <DetailWrapper>
      {/* Header */}
      <DetailHeader>
        <TitleRow>{post.notice_title || '(제목 없음)'}</TitleRow>
        <MetaRow>
          <MetaLeft>
            <AuthorBadge>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {post.notice_name}
            </AuthorBadge>
            <MetaItem>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {post.search_count || 0}
            </MetaItem>
          </MetaLeft>
          <ActionBtns>
            {isOwner && (
              <>
                <ActionBtn onClick={onEdit}>✏️ 수정</ActionBtn>
                <ActionBtn $variant="danger" onClick={onDelete}>
                  🗑 삭제
                </ActionBtn>
              </>
            )}
            <CloseBtn onClick={onClose}>✕ 닫기</CloseBtn>
          </ActionBtns>
        </MetaRow>
      </DetailHeader>

      {/* Body (HTML 렌더링) */}
      <DetailBody dangerouslySetInnerHTML={{ __html: post.notice_contents }} />

      {/* Comments */}
      <CommentsSection>
        <CommentsHeader>
          💬 댓글 <CommentCount>{comments.length}</CommentCount>
        </CommentsHeader>

        {comments.length > 0 && (
          <CommentList>
            {comments.map((comment, i) => (
              <CommentItem key={comment.num || i}>
                <CommentTop>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CommentAuthor>{comment.user_name}</CommentAuthor>
                    <CommentTime>{comment.create_time}</CommentTime>
                  </div>
                  {session?.UserId === comment.user_id && (
                    <CommentDeleteBtn onClick={() => onDeleteComment?.(comment)}>
                      삭제
                    </CommentDeleteBtn>
                  )}
                </CommentTop>
                <CommentBody>{comment.contents}</CommentBody>
              </CommentItem>
            ))}
          </CommentList>
        )}

        {/* Comment Input */}
        {isLoggedIn ? (
          <CommentInputWrapper>
            <CommentInputArea>
              <CommentTextarea
                ref={commentRef}
                placeholder="댓글을 입력하세요... (Ctrl+Enter로 등록)"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
              />
              <CommentSubmitBtn
                disabled={!commentText.trim()}
                onClick={handleSubmitComment}
              >
                등록
              </CommentSubmitBtn>
            </CommentInputArea>
          </CommentInputWrapper>
        ) : (
          <LoginNotice>
            🔒 로그인을 해야 댓글 작성이 가능합니다.
          </LoginNotice>
        )}
      </CommentsSection>
    </DetailWrapper>
  );
}

export default BoardDetail;
